import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Search, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Pagination } from '../components/Pagination';
import { jobService, type JobItem } from '../services/jobService';
import { fieldClass, labelClass, formatDateTime } from '../utils/form';

type JobSort = 'score' | 'date' | 'published';

const PAGE_SIZE = 50;

const sortLabel: Record<JobSort, string> = {
  score: 'امتیاز تطبیق',
  date: 'تاریخ ذخیره',
  published: 'تاریخ انتشار',
};

export function JobsPage() {
  const [items, setItems] = useState<JobItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  // Recommended list uses the profile floor (70). "all" keeps low scores for debugging.
  const [view, setView] = useState<'recommended' | 'all'>('recommended');
  const [minScore, setMinScore] = useState(70);
  const [source, setSource] = useState('');
  const [sort, setSort] = useState<JobSort>('score');
  const [selected, setSelected] = useState<Record<string, unknown> | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await jobService.listJobs({
        page,
        limit: PAGE_SIZE,
        q: q.trim() || undefined,
        minScore: view === 'all' ? undefined : minScore,
        view,
        source: source || undefined,
        sort,
      });
      setItems(res?.data ?? []);
      setTotal(res?.total ?? 0);
      const pages =
        res?.totalPages ??
        Math.max(1, Math.ceil((res?.total ?? 0) / (res?.limit || PAGE_SIZE)));
      setTotalPages(pages);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت آگهی‌ها');
    } finally {
      setLoading(false);
    }
  }, [page, q, minScore, view, source, sort]);

  useEffect(() => {
    void load();
  }, [load]);

  const applyFilters = () => {
    if (page !== 1) setPage(1);
    else void load();
  };

  const openJob = async (id: string) => {
    try {
      const item = await jobService.getJob(id);
      setSelected(item ?? null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا');
    }
  };

  const onDelete = async (id: string, title: string) => {
    if (!window.confirm(`آگهی «${title || id}» حذف شود؟`)) return;
    setDeletingId(id);
    try {
      await jobService.deleteJob(id);
      toast.success('آگهی حذف شد');
      if (selected && String(selected.id) === id) setSelected(null);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در حذف');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">آگهی‌های شغلی</h1>
          <p className="mt-1 text-sm text-slate-500">
            مرتب‌شده بر اساس {sortLabel[sort]}
            {!loading ? ` · ${total.toLocaleString('fa-IR')} مورد` : ''}
            {!loading && totalPages > 1
              ? ` · صفحه ${page.toLocaleString('fa-IR')} از ${totalPages.toLocaleString('fa-IR')}`
              : ''}
          </p>
        </div>
        <Link to="/job-search" className="text-sm font-semibold text-rose-600 hover:underline">
          ← جستجوی جدید
        </Link>
      </div>

      <div className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:grid-cols-2 lg:grid-cols-5">
        <div className="sm:col-span-2">
          <label className={labelClass}>جستجو</label>
          <input className={fieldClass} value={q} onChange={(e) => setQ(e.target.value)} placeholder="عنوان یا شرکت" />
        </div>
        <div>
          <label className={labelClass}>حداقل امتیاز</label>
          <input
            type="number"
            className={fieldClass}
            value={view === 'all' ? 0 : minScore}
            disabled={view === 'all'}
            onChange={(e) => setMinScore(Number(e.target.value) || 0)}
          />
        </div>
        <div>
          <label className={labelClass}>نمایش</label>
          <select
            className={fieldClass}
            value={view}
            onChange={(e) => {
              setView(e.target.value as 'recommended' | 'all');
              setPage(1);
            }}
          >
            <option value="recommended">پیشنهادی</option>
            <option value="all">همه (عیب‌یابی)</option>
          </select>
        </div>
        <div>
          <label className={labelClass}>منبع</label>
          <select
            className={fieldClass}
            value={source}
            onChange={(e) => {
              setSource(e.target.value);
              setPage(1);
            }}
          >
            <option value="">همه</option>
            <option value="jobinja">Jobinja</option>
            <option value="jobvision">JobVision</option>
          </select>
        </div>
        <div>
          <label className={labelClass}>مرتب‌سازی</label>
          <select
            className={fieldClass}
            value={sort}
            onChange={(e) => {
              setSort(e.target.value as JobSort);
              setPage(1);
            }}
          >
            <option value="score">امتیاز</option>
            <option value="date">تاریخ ذخیره</option>
            <option value="published">تاریخ انتشار</option>
          </select>
        </div>
        <div className="sm:col-span-2 lg:col-span-5">
          <button
            type="button"
            onClick={applyFilters}
            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-bold text-white"
          >
            <Search className="h-4 w-4" />
            اعمال فیلتر
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-3 py-2 text-right font-semibold">امتیاز</th>
              <th className="px-3 py-2 text-right font-semibold">عنوان</th>
              <th className="px-3 py-2 text-right font-semibold">شرکت</th>
              <th className="px-3 py-2 text-right font-semibold">منبع</th>
              <th className="px-3 py-2 text-right font-semibold">مکان</th>
              <th className="px-3 py-2 text-right font-semibold">سطح</th>
              <th className="px-3 py-2 text-right font-semibold">زمان</th>
              <th className="px-3 py-2 text-right font-semibold" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-slate-400">
                  در حال بارگذاری…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-3 py-8 text-center text-slate-400">
                  {view === 'recommended'
                    ? `آگهی پیشنهادی با امتیاز ≥ ${minScore} نیست. برای دیدن امتیازهای پایین، نمایش را روی «همه» بگذار.`
                    : 'آگهی‌ای نیست — اول یک جستجو اجرا کنید'}
                </td>
              </tr>
            ) : (
              items.map((j) => (
                <tr key={j.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2">
                    {j.matchKind === 'rejected' ? (
                      <span className="text-xs font-semibold text-rose-700">رد</span>
                    ) : null}
                    <span
                      className={`inline-flex min-w-[2.5rem] justify-center rounded-lg px-2 py-1 text-xs font-bold ${
                        j.matchScore >= 70
                          ? 'bg-emerald-100 text-emerald-800'
                          : j.matchScore >= 40
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-slate-100 text-slate-600'
                      }`}
                    >
                      {j.matchScore}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-medium text-slate-900">
                    <button type="button" className="text-right hover:underline" onClick={() => void openJob(j.id)}>
                      {j.title || '—'}
                    </button>
                  </td>
                  <td className="px-3 py-2">{j.company || '—'}</td>
                  <td className="px-3 py-2 text-xs">
                    <span className="rounded-lg bg-slate-100 px-2 py-0.5 font-medium text-slate-700">
                      {j.sourceSlug || '—'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {j.locationNormalized}
                    {j.workArrangement !== 'unknown' ? ` · ${j.workArrangement}` : ''}
                  </td>
                  <td className="px-3 py-2 text-xs">{j.seniority}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{formatDateTime(j.createdAt)}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-2">
                      {j.canonicalUrl ? (
                        <a
                          href={j.canonicalUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex text-rose-600"
                          title="مشاهده در منبع"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      ) : null}
                      <button
                        type="button"
                        disabled={deletingId === j.id}
                        onClick={() => void onDelete(j.id, j.title)}
                        className="inline-flex text-slate-400 hover:text-rose-600 disabled:opacity-50"
                        title="حذف آگهی"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 ? (
        <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
      ) : null}

      {selected ? (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center" onClick={() => setSelected(null)}>
          <div
            className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-slate-900">{String(selected.title ?? '')}</h2>
                <p className="text-sm text-slate-500">{String(selected.company ?? '')}</p>
              </div>
              <button type="button" className="text-slate-400" onClick={() => setSelected(null)}>
                ✕
              </button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              <span className="rounded-lg bg-emerald-50 px-2 py-1 font-bold text-emerald-800">
                امتیاز {String(selected.matchScore ?? 0)}
              </span>
              <span className="rounded-lg bg-slate-100 px-2 py-1">{String(selected.sourceSlug ?? '')}</span>
              <span className="rounded-lg bg-slate-100 px-2 py-1">{String(selected.locationNormalized)}</span>
              <span className="rounded-lg bg-slate-100 px-2 py-1">{String(selected.seniority)}</span>
              <span className="rounded-lg bg-slate-100 px-2 py-1">{String(selected.workArrangement)}</span>
            </div>
            {selected.matchReasoning ? (
              <p className="mt-3 text-sm text-slate-600">{String(selected.matchReasoning)}</p>
            ) : null}
            <div className="mt-4 whitespace-pre-wrap text-sm leading-6 text-slate-700">
              {String(selected.description ?? '').slice(0, 4000)}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              {selected.canonicalUrl ? (
                <a
                  href={String(selected.canonicalUrl)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-rose-600"
                >
                  مشاهده در منبع <ExternalLink className="h-4 w-4" />
                </a>
              ) : null}
              <button
                type="button"
                disabled={deletingId === String(selected.id)}
                onClick={() => void onDelete(String(selected.id), String(selected.title ?? ''))}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-rose-600 disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4" />
                حذف آگهی
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
